<template>
    <modal v-model="mutableModalOpened" @hide="closeModal" size="lg">
        <span slot="title" class="margin-left margin-right">
            <em>Settings</em>
        </span>

        <api-errors :errors="errors" />

        <div class="container-fluid">
            <div class="row">
                <div class="col-xs-4">
                    <table class="table">
                        <colgroup>
                            <col span="1" class="table-browser-width-30-percent">
                        </colgroup>
                        <tr>
                            <td class="table-browser-setting-margin">
                                <strong>Page length:</strong>
                            </td>
                            <td class="table-browser-setting-margin dropdown">
                                <button type="button" class="btn btn-default btn-sm dropdown-toggle"
                                        data-toggle="dropdown" aria-haspopup="true" aria-expanded="false"
                                >
                                    {{ mutablePageLength }} <span class="caret" />
                                </button>
                                <ul class="dropdown-menu table-browser-dropdown-page-length">
                                    <li v-for="(pageLength, index) in pageLengthOptions" :key="index"
                                        class="table-browser-dropdown-option-margin"
                                    >
                                        <input type="radio"
                                               :id="'radio-' + index"
                                               :value="pageLength"
                                               v-model="mutablePageLength"
                                        >
                                        <label :for="'radio-' + index" class="table-browser-dropdown-option-margin-left">
                                            {{ pageLength }}
                                        </label>
                                    </li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td class="table-browser-setting-margin">
                                <strong>Include alternative names:</strong>
                            </td>
                            <td class="table-browser-setting-margin">
                                <input v-if="hasANT" type="checkbox" ref="include-an"
                                       v-model="mutableAlternativeNameSetting.included"
                                       @change="updateSelectedANT"
                                >
                                <input v-else type="checkbox" ref="include-an"
                                       v-model="mutableAlternativeNameSetting.included"
                                       disabled data-toggle="tooltip" title="No alternative name available"
                                >
                            </td>
                            <td>
                                <span v-if="mutableAlternativeNameSetting.included" ref="selected-ant"> {{ selectedANT }} </span>
                            </td>
                        </tr>
                        <tr v-if="mutableAlternativeNameSetting.included">
                            <td>
                                <ul class="table-browser-dropdown-menu table-browser-dropdown-alternative-name">
                                    <li v-for="(item, index) in alternativeNameTypes" :key="index">
                                        <input type="radio" ref="ant-option" :value="item[0]"
                                               v-model="mutableAlternativeNameSetting.selected"
                                               @change="updateSelectedANT"
                                        >
                                        {{ item[1] }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td class="table-browser-setting-margin">
                                <strong>Include federated items:</strong>
                            </td>
                            <td class="table-browser-setting-margin">
                                <input type="checkbox" v-model="showFederated">
                            </td>
                        </tr>
                    </table>
                </div>

                <div class="col-xs-4 table-browser-border-shadow">
                    <h3> Select visible columns </h3>
                    <table class="table">
                        <tr v-for="(value, column) in mutableVisibleColumnOptions" :key="value.id">
                            <td class="padding-top-bottom-small">
                                <strong>{{ value.verbose }}</strong>
                            </td>
                            <td class="padding-top-bottom-small">
                                <input
                                    v-model="mutableVisibleColumnOptions[column].selected"
                                    :id="column"
                                    :disabled="checkDisable(column)"
                                    ref="visible-column-option"
                                    type="checkbox"
                                    @change="updateVisibilityOptions($event.target, 'column')"
                                >
                            </td>
                        </tr>
                    </table>
                </div>

                <div class="col-xs-4">
                    <h3> Details column options </h3>
                    <table class="table">
                        <tr v-for="(value, option) in mutableVisibleDetailOptions" :key="value.id">
                            <td class="padding-top-bottom-small">
                                <strong>{{ value.verbose }}</strong>
                            </td>
                            <td class="padding-top-bottom-small">
                                <input
                                    v-model="mutableVisibleDetailOptions[option].selected"
                                    :id="option"
                                    :disabled="!mutableVisibleColumnOptions.shortDefinition.selected"
                                    ref="details-column-option"
                                    type="checkbox"
                                    @change="updateVisibilityOptions($event.target, 'detail')"
                                >
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
        </div>

        <span slot="footer">
            <button type="button" class="btn btn-default pull-left" ref="default-btn" @click="applyDefaultSettings">
                <i class="fa fa-refresh" aria-hidden="true" /> Reset to default settings
            </button>

            <button type="button" class="btn btn-default" ref="cancel-btn" @click="closeModal">
                <i class="fa fa-times" aria-hidden="true" /> Cancel
            </button>

            <button type="button" class="btn btn-primary" ref="apply-btn" @click="applySettings">
                <i class="fa fa-floppy-o" aria-hidden="true" /> Apply
            </button>
        </span>
    </modal>
</template>

<script>
import Modal from 'uiv/src/components/modal/Modal.vue'
import ApiErrors from '@/ApiErrorDisplay.vue'

export default {
    name: "TableSetting",
    components: {
        Modal,
        ApiErrors
    },
    props: {
        initialPageLength: {
            type: Number,
            required: true,
        },

        initialModalOpened: {
            type: Boolean,
            required: true
        },

        initialVisibleColumnOptions: {
            type: Object,
            required: true,
        },

        initialVisibleDetailOptions: {
            type: Object,
            required: true,
        },

        initialAlternativeNameSetting: {
            type: Object,
            required: true,
        },

        initShowFederated: {
            type: Boolean,
            required: true,
        },

        alternativeNameTypes: {
            type: Array,
            required: true,
        },
    },

    data: () => ({
        mutablePageLength: 20,
        mutableModalOpened: false,
        mutableVisibleColumnOptions: {},
        mutableVisibleDetailOptions: {},
        mutableAlternativeNameSetting: {},
        showFederated: false,
        selectedANT: '',
        pageLengthOptions: [10, 20, 50, 100],
        errors: {}
    }),

    computed: {
        hasANT() {
            return Object.keys(this.alternativeNameTypes).length !== 0 && this.alternativeNameTypes.constructor === Array
        },
    },

    beforeMount() {
        this.setInitialSelectedANT()
        this.mutablePageLength = this.initialPageLength
        this.mutableModalOpened = this.initialModalOpened
        this.showFederated = this.initShowFederated

        /* Need to clone the Object Props because objects and arrays in JavaScript are passed by references,
        so if the prop is an array or object, mutating the object or array itself inside the child
        will affect parent state

        JSON.parse(JSON.stringify()) might look weird, but it is an acceptable way to deep clone objects in javascript
        */
        this.mutableVisibleColumnOptions = JSON.parse(JSON.stringify(this.initialVisibleColumnOptions))
        this.mutableVisibleDetailOptions = JSON.parse(JSON.stringify(this.initialVisibleDetailOptions))
    },
    methods: {
        closeModal: function () {
            this.$emit('close-modal')
        },

        checkDisable: function (column) {
            return column === "name"
        },

        setInitialSelectedANT: function () {
            this.mutableAlternativeNameSetting = JSON.parse(JSON.stringify(this.initialAlternativeNameSetting));
            if (this.mutableAlternativeNameSetting.included) {
                this.selectedANT = this.getANTByUuid(this.mutableAlternativeNameSetting.selected)
            }
        },

        updateVisibilityOptions: function (target, optionType) {
            if (optionType == "column") {
                this.mutableVisibleColumnOptions[target.id].selected = target.checked
            } else {
                this.mutableVisibleDetailOptions[target.id].selected = target.checked
            }
        },

        applySettings: function () {
            if (this.mutablePageLength >= 1 && this.mutablePageLength <= 100) {
                this.closeModal()
                this.$emit('apply-settings', {
                    'pageLength': this.mutablePageLength,
                    'visibleColumns': this.mutableVisibleColumnOptions,
                    'visibleDetailOptions': this.mutableVisibleDetailOptions,
                    'alternativeNameSetting': this.mutableAlternativeNameSetting,
                    'showFederated': this.showFederated
                })
            } else {
                this.errors = {
                    "Validation Error": " Page length can not be smaller than 1 or larger than 100"
                }
            }
        },

        applyDefaultSettings: function () {
            this.closeModal()
            this.$emit('apply-default-settings', {})
        },

        getANTByUuid: function (uuid) {
            for (let i = 0; i < this.alternativeNameTypes.length; i++) {
                if (this.alternativeNameTypes[i][0] == uuid) {
                    return this.alternativeNameTypes[i][1]
                }
            }
            return uuid + ' is not a valid alternative name type uuid'
        },

        updateSelectedANT: function () {
            if (this.mutableAlternativeNameSetting.included && this.hasANT) {
                if (typeof this.mutableAlternativeNameSetting.selected !== "undefined") {
                    this.selectedANT = this.getANTByUuid(this.mutableAlternativeNameSetting.selected)
                } else{
                    // Select the first alternative name type when nothing is selected yet
                    this.mutableAlternativeNameSetting.selected = this.alternativeNameTypes[0][0]
                    this.selectedANT = this.alternativeNameTypes[0][1]
                }
            }
        }
    }
}
</script>
